import React, { createContext, useContext, useState, useCallback } from "react";
import eventService, { Event } from "../services/api/eventService";

interface EventContextState {
  // Event data
  featuredEvents: Event[];
  upcomingEvents: Event[];
  userEvents: Event[];

  // Loading states
  isFeaturedLoading: boolean;
  isUpcomingLoading: boolean;
  isUserEventsLoading: boolean;

  // Error states
  featuredError: string | null;
  upcomingError: string | null;
  userEventsError: string | null;

  // Pagination states
  hasMoreUpcomingEvents: boolean;

  // Actions
  refreshFeaturedEvents: () => Promise<void>;
  refreshUpcomingEvents: () => Promise<void>;
  loadMoreUpcomingEvents: () => Promise<void>;
  refreshUserEvents: () => Promise<void>;

  // Event management
  createEvent: (data: any) => Promise<Event>;
  updateEvent: (eventId: string, data: any) => Promise<Event>;
  deleteEvent: (eventId: string) => Promise<void>;

  // RSVP actions
  markAsGoing: (eventId: string) => Promise<void>;
  markAsInterested: (eventId: string) => Promise<void>;
  removeRSVP: (eventId: string) => Promise<void>;

  // Search states
  searchLoading: boolean;
  searchError: string | null;
  searchResults: Event[];

  // Search methods
  searchEvents: (query: string) => Promise<void>;
  clearSearch: () => void;

  // For compatibility with components expecting an 'events' property
  events: Event[];
}

const EventContext = createContext<EventContextState | undefined>(undefined);

export const EventProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Event data states
  const [featuredEvents, setFeaturedEvents] = useState<Event[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [userEvents, setUserEvents] = useState<Event[]>([]);

  // Loading states
  const [isFeaturedLoading, setIsFeaturedLoading] = useState(true);
  const [isUpcomingLoading, setIsUpcomingLoading] = useState(true);
  const [isUserEventsLoading, setIsUserEventsLoading] = useState(true);

  // Error states
  const [featuredError, setFeaturedError] = useState<string | null>(null);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);
  const [userEventsError, setUserEventsError] = useState<string | null>(null);

  // Search states
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<Event[]>([]);

  // Load featured events
  const loadFeaturedEvents = useCallback(async () => {
    setIsFeaturedLoading(true);
    setFeaturedError(null);

    try {
      const { events: fetchedEvents } = await eventService.getEvents({
        limit: 5,
        category: "festival", // Updated from 'type' to 'category'
      });
      setFeaturedEvents(fetchedEvents);
    } catch (err) {
      setFeaturedError(
        err instanceof Error ? err.message : "Failed to load featured events",
      );
    } finally {
      setIsFeaturedLoading(false);
    }
  }, []);

  const [upcomingPage, setUpcomingPage] = useState(1);
  const [hasMoreUpcomingEvents, setHasMoreUpcomingEvents] = useState(true);

  // Load upcoming events (initial)
  const loadUpcomingEvents = useCallback(async () => {
    setIsUpcomingLoading(true);
    setUpcomingError(null);
    setUpcomingPage(1);

    try {
      const { events: fetchedEvents, total } = await eventService.getEvents({
        limit: 10,
        page: 1,
        upcoming: true,
      });
      setUpcomingEvents(fetchedEvents);
      setHasMoreUpcomingEvents(fetchedEvents.length < total);
    } catch (err) {
      setUpcomingError(
        err instanceof Error ? err.message : "Failed to load upcoming events",
      );
    } finally {
      setIsUpcomingLoading(false);
    }
  }, []);

  // Load more upcoming events
  const loadMoreUpcomingEvents = useCallback(async () => {
    if (isUpcomingLoading || !hasMoreUpcomingEvents) return;

    setIsUpcomingLoading(true);
    setUpcomingError(null);

    try {
      const nextPage = upcomingPage + 1;
      const { events: fetchedEvents, total } = await eventService.getEvents({
        limit: 10,
        page: nextPage,
        upcoming: true,
      });

      setUpcomingEvents((prev) => [...prev, ...fetchedEvents]);
      setUpcomingPage(nextPage);
      setHasMoreUpcomingEvents(
        upcomingEvents.length + fetchedEvents.length < total,
      );
    } catch (err) {
      setUpcomingError(
        err instanceof Error
          ? err.message
          : "Failed to load more upcoming events",
      );
    } finally {
      setIsUpcomingLoading(false);
    }
  }, [
    upcomingPage,
    hasMoreUpcomingEvents,
    isUpcomingLoading,
    upcomingEvents.length,
  ]);

  // Load user events
  const loadUserEvents = useCallback(async (userId?: string) => {
    if (!userId) {
      setUserEvents([]);
      return;
    }

    setIsUserEventsLoading(true);
    setUserEventsError(null);

    try {
      const { events: fetchedEvents } = await eventService.getUserEvents(
        userId,
        {
          limit: 10,
          type: "attending",
        },
      );
      setUserEvents(fetchedEvents);
    } catch (err) {
      setUserEventsError(
        err instanceof Error ? err.message : "Failed to load user events",
      );
    } finally {
      setIsUserEventsLoading(false);
    }
  }, []);

  // Initial data load - Removed to prevent redundant fetching on every page navigation
  // useEffect(() => {
  //   const fetchEvents = async () => {
  //     try {
  //       setIsFeaturedLoading(true);
  //       setIsUpcomingLoading(true);
  //       setIsUserEventsLoading(true);
  //       setFeaturedError(null);
  //       setUpcomingError(null);
  //       setUserEventsError(null);
  //
  //        const { events: fetchedEvents }: any = await eventService.getEvents();
  //       setFeaturedEvents(fetchedEvents);
  //       setUpcomingEvents(fetchedEvents);
  //       setUserEvents(fetchedEvents);
  //     } catch (error) {
  //       console.error('Failed to fetch events:', error);
  //       setFeaturedError('Failed to load events. Please try again later.');
  //       setUpcomingError('Failed to load events. Please try again later.');
  //       setUserEventsError('Failed to load events. Please try again later.');
  //       setFeaturedEvents([]);
  //       setUpcomingEvents([]);
  //       setUserEvents([]);
  //     } finally {
  //       setIsFeaturedLoading(false);
  //       setIsUpcomingLoading(false);
  //       setIsUserEventsLoading(false);
  //     }
  //   };

  //   fetchEvents();
  // }, []);

  // Event management functions
  const createEvent = async (data: any) => {
    try {
      const newEvent = await eventService.createEvent(data);
      setUpcomingEvents((prev) => [newEvent, ...prev]);
      return newEvent;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to create event",
      );
    }
  };

  const updateEvent = async (eventId: string, data: any) => {
    try {
      const updatedEvent = await eventService.updateEvent(eventId, data);

      // Update event in all relevant lists
      const updateEventInList = (events: Event[]) =>
        events.map((event) => (event.id === eventId ? updatedEvent : event));

      setFeaturedEvents(updateEventInList);
      setUpcomingEvents(updateEventInList);
      setUserEvents(updateEventInList);

      return updatedEvent;
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to update event",
      );
    }
  };

  const deleteEvent = async (eventId: string) => {
    try {
      await eventService.deleteEvent(eventId);

      // Remove event from all lists
      const removeEventFromList = (events: Event[]) =>
        events.filter((event) => event.id !== eventId);

      setFeaturedEvents(removeEventFromList);
      setUpcomingEvents(removeEventFromList);
      setUserEvents(removeEventFromList);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to delete event",
      );
    }
  };

  // RSVP functions
  const markAsGoing = async (eventId: string) => {
    try {
      const result = await eventService.rsvpGoing(eventId);

      // Update attendee count and status in all lists
      const updateList = (events: Event[]) =>
        events.map((event) => {
          if (event.id === eventId) {
            return {
              ...event,
              attendeeCount: result.attendeeCount,
              userRsvpStatus: result.userRsvpStatus as any,
            };
          }
          return event;
        });

      setFeaturedEvents(updateList);
      setUpcomingEvents(updateList);
      setUserEvents(updateList);
      setSearchResults(updateList);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to mark as going",
      );
    }
  };

  const markAsInterested = async (eventId: string) => {
    try {
      const result = await eventService.rsvpInterested(eventId);

      // Update interested count and status in all lists
      const updateList = (events: Event[]) =>
        events.map((event) => {
          if (event.id === eventId) {
            return {
              ...event,
              attendeeCount: result.attendeeCount,
              userRsvpStatus: result.userRsvpStatus as any,
            };
          }
          return event;
        });

      setFeaturedEvents(updateList);
      setUpcomingEvents(updateList);
      setUserEvents(updateList);
      setSearchResults(updateList);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to mark as interested",
      );
    }
  };

  const removeRSVP = async (eventId: string) => {
    try {
      const result = await eventService.cancelRsvp(eventId);

      // Update status and count in all lists
      const updateList = (events: Event[]) =>
        events.map((event) => {
          if (event.id === eventId) {
            return {
              ...event,
              attendeeCount: result.attendeeCount,
              userRsvpStatus: null,
            };
          }
          return event;
        });

      setFeaturedEvents(updateList);
      setUpcomingEvents(updateList);
      setUserEvents(updateList);
      setSearchResults(updateList);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to remove RSVP",
      );
    }
  };

  // Search methods
  const searchEvents = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    try {
      const { events } = await eventService.searchEvents({ query });
      setSearchResults(events);
    } catch (error) {
      setSearchError("Failed to search events");
      console.error("Search events error:", error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchError(null);
  }, []);

  const value: EventContextState = {
    // Event data
    featuredEvents,
    upcomingEvents,
    userEvents,

    // For compatibility with components expecting an 'events' property
    events: (searchResults.length > 0 ? searchResults : upcomingEvents) as any,

    // Pagination states
    hasMoreUpcomingEvents,

    // Loading states
    isFeaturedLoading,
    isUpcomingLoading,
    isUserEventsLoading,

    // Error states
    featuredError,
    upcomingError,
    userEventsError,

    // Refresh functions
    refreshFeaturedEvents: loadFeaturedEvents,
    refreshUpcomingEvents: loadUpcomingEvents,
    loadMoreUpcomingEvents,
    refreshUserEvents: loadUserEvents,

    // Event management
    createEvent,
    updateEvent,
    deleteEvent,

    // RSVP actions
    markAsGoing,
    markAsInterested,
    removeRSVP,

    // Search states
    searchLoading,
    searchError,
    searchResults,
    searchEvents,
    clearSearch,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

export const useEvents = () => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error("useEvents must be used within an EventProvider");
  }
  return context;
};

export default EventContext;
